import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { SupportApiService } from '../core/support-api.service';
import { TicketStatus } from '../core/models';
import { isResolvable } from '../core/ticket-status';

/** Resolve control. PATCHes `target_status: 'resolved'`, which runs apply_transition
 *  server-side: the status write, the auto-close clock, and a hash-chained
 *  ticket_transitions row recorded under the agent's own identity. Closing is
 *  system-only (auto-close after the SLA policy's window), so there is no Close here. */
@Component({
  selector: 'app-resolve-control',
  standalone: true,
  imports: [NgbTooltip],
  template: `
    <div class="resolve">
      <button type="button" class="bt-btn bt-btn--primary" data-testid="resolve-submit"
              [disabled]="!canResolve()" (click)="resolve()" [ngbTooltip]="tooltip()">
        {{ busy() ? 'Resolving…' : 'Resolve' }}
      </button>
      @if (error()) {
        <span class="resolve__error" data-testid="resolve-error" role="alert">{{ error() }}</span>
      }
    </div>
  `,
  styles: [`
    .resolve { display: flex; flex-direction: column; align-items: flex-end; gap: 0.35rem; }
    .resolve__error { font-size: 12px; color: var(--bt-status-danger); max-width: 220px; text-align: right; }
  `],
})
export class ResolveControlComponent {
  private api = inject(SupportApiService);

  @Input() ticketId = '';
  @Input() currentStatus: TicketStatus | '' = '';
  @Output() resolved = new EventEmitter<void>();

  busy = signal(false);
  error = signal<string | null>(null);

  canResolve(): boolean {
    return !this.busy() && isResolvable(this.currentStatus);
  }

  tooltip(): string {
    if (this.currentStatus === 'resolved') return 'Already resolved — it closes automatically after the auto-close window';
    if (this.currentStatus === 'closed') return 'Closed tickets cannot be changed';
    return 'Mark this ticket resolved. It closes automatically after the auto-close window; the change is recorded in History under your name';
  }

  resolve(): void {
    if (!this.canResolve()) return;
    this.busy.set(true);
    this.error.set(null);
    this.api.patchTicket(this.ticketId, { target_status: 'resolved' }).subscribe({
      next: () => {
        this.busy.set(false);
        this.resolved.emit();
      },
      error: (err: HttpErrorResponse) => {
        this.busy.set(false);
        this.error.set(err.status === 409
          ? 'This ticket changed since it was opened. Reload the page and try again.'
          : 'Could not resolve this ticket. Try again.');
      },
    });
  }
}
